// The Calendar tab's simple screens: each one does one job (first-run setup asks one question per screen).
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, DatePicker, Dropdown, Input, Modal, Popconfirm, Select, Spin, Switch, message } from "antd";
import { CalendarOutlined, ClockCircleOutlined, CloseOutlined, GoogleOutlined, MoreOutlined, PlusOutlined, WindowsOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { cancelBooking, connectCalendly, createBooking, listBookings, markNoShow, previewSlots, rescheduleBooking } from "./calendarService";
import SlotPicker from "./SlotPicker";
import { DAYS, TIMES, TIME_OPTIONS, formatLocal, newServiceId, zonedIso } from "./calendarShared";
import styles from "./style.module.scss";

const Step = ({ step, t }) => (step ? <div className={styles.stepLabel}>{t("sonic_calendar.step_of", { step, total: 4 })}</div> : null);

const Back = ({ onBack, label }) =>
  onBack ? (
    <button type="button" className={styles.back} onClick={onBack}>
      ‹ {label}
    </button>
  ) : null;

/** Next free range after a day's last one: an hour later, two hours long (Calendly's "+"). */
const nextRange = (ranges) => {
  const last = TIMES.indexOf(ranges[ranges.length - 1][1]);
  const from = Math.min(last + 4, TIMES.length - 2);
  return from > last ? [TIMES[from], TIMES[Math.min(from + 8, TIMES.length - 1)]] : null;
};

/** "When are you open?": like Calendly, each day on or off with its own hours (and breaks). */
export const HoursScreen = ({ weekly, step, saving, onDone, onBack, t, title, backLabel }) => {
  const [week, setWeek] = useState(() => Object.fromEntries(DAYS.map((d) => [d, (weekly[d] || []).map((r) => [...r])])));
  const setDay = (day, ranges) => setWeek((w) => ({ ...w, [day]: ranges }));
  const lastOpen = (day) => [...DAYS.slice(0, DAYS.indexOf(day)).reverse(), ...DAYS].find((d) => week[d].length);
  const toggle = (day) => {
    if (week[day].length) return setDay(day, []);
    const like = lastOpen(day);
    return setDay(day, like ? week[like].map((r) => [...r]) : [["09:00", "18:00"]]);
  };
  const valid = DAYS.some((d) => week[d].length) && DAYS.every((d) => week[d].every(([a, b]) => b > a));

  return (
    <div className={styles.simple}>
      <Back onBack={onBack} label={backLabel || t(step ? "sonic_calendar.public_back" : "sonic_calendar.settings")} />
      <Step step={step} t={t} />
      <h1 className={styles.simpleTitle}>{title || t("sonic_calendar.q_hours")}</h1>
      <div className={styles.week}>
        {DAYS.map((d) => {
          const ranges = week[d];
          const extra = ranges.length ? nextRange(ranges) : null;
          return (
            <div key={d} className={styles.weekRow}>
              <button type="button" aria-pressed={ranges.length > 0} className={ranges.length ? styles.dayToggleOn : styles.dayToggle} onClick={() => toggle(d)}>
                {t(`sonic_prospect.handoff_day_${d}`)}
              </button>
              <div className={styles.weekRanges}>
                {ranges.length === 0 ? <span className={styles.weekClosed}>{t("sonic_calendar.closed")}</span> : null}
                {ranges.map((range, idx) => (
                  <div key={idx} className={styles.timeRow}>
                    <Select
                      className={styles.bigField}
                      suffixIcon={null}
                      value={range[0]}
                      options={TIME_OPTIONS.slice(0, -1)}
                      aria-label={t("sonic_calendar.from")}
                      onChange={(v) => setDay(d, ranges.map((r, i) => (i === idx ? [v, r[1]] : r)))}
                    />
                    <span>{t("sonic_calendar.to_word")}</span>
                    <Select
                      className={styles.bigField}
                      suffixIcon={null}
                      value={range[1]}
                      options={TIME_OPTIONS.slice(1)}
                      aria-label={t("sonic_calendar.to")}
                      onChange={(v) => setDay(d, ranges.map((r, i) => (i === idx ? [r[0], v] : r)))}
                    />
                    {idx > 0 ? (
                      <button type="button" className={styles.quietIcon} aria-label={t("sonic_calendar.remove")} onClick={() => setDay(d, ranges.filter((_, i) => i !== idx))}>
                        <CloseOutlined />
                      </button>
                    ) : null}
                    {idx === ranges.length - 1 && extra && ranges.length < 4 ? (
                      <button type="button" className={styles.quietIcon} aria-label={t("sonic_calendar.add_range")} title={t("sonic_calendar.add_range")} onClick={() => setDay(d, [...ranges, extra])}>
                        <PlusOutlined />
                      </button>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <div>
        <Button type="primary" size="large" className={styles.cta} disabled={!valid} loading={saving} onClick={() => onDone(week)}>
          {t(step ? "sonic_calendar.next" : "sonic_calendar.save")}
        </Button>
      </div>
    </div>
  );
};

const LENGTHS = [15, 20, 30, 45, 60, 90];

/** "How long is each appointment?": one length; the open hours are split into times this long. */
export const LengthScreen = ({ minutes, step, saving, onDone, onBack, t }) => {
  const [length, setLength] = useState(minutes || 30);
  const options = [...new Set([...LENGTHS, minutes || 30])].sort((x, y) => x - y);
  const example = [0, 1, 2, 3].map((i) => {
    const m = 9 * 60 + i * length;
    return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
  });

  return (
    <div className={styles.simple}>
      <Back onBack={onBack} label={t(step ? "sonic_calendar.public_back" : "sonic_calendar.settings")} />
      <Step step={step} t={t} />
      <h1 className={styles.simpleTitle}>{t("sonic_calendar.q_length")}</h1>
      <div className={styles.dayToggles}>
        {options.map((v) => (
          <button key={v} type="button" aria-pressed={v === length} className={v === length ? styles.lengthOn : styles.length} onClick={() => setLength(v)}>
            {t("sonic_calendar.minutes", { count: v })}
          </button>
        ))}
      </div>
      <p className={styles.hint}>{t("sonic_calendar.length_example", { times: `${example.join(", ")} …` })}</p>
      <div>
        <Button type="primary" size="large" className={styles.cta} loading={saving} onClick={() => onDone(length)}>
          {t(step ? "sonic_calendar.next" : "sonic_calendar.save")}
        </Button>
      </div>
    </div>
  );
};

const cleanSlug = (v) =>
  v
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/-{2,}/g, "-")
    .slice(0, 48);

/** "Your booking link": pivony.com/calendar/<name>, for the business's website, bio or WhatsApp. */
export const LinkScreen = ({ base, slug, savedUrl, step, saving, onDone, onBack, t }) => {
  const [value, setValue] = useState(slug || "");
  const prefix = `${(base || "").replace(/^https?:\/\//, "")}/`;
  const name = value.replace(/^-+|-+$/g, "");
  const valid = name.length >= 3;

  return (
    <div className={styles.simple}>
      <Back onBack={onBack} label={t(step ? "sonic_calendar.public_back" : "sonic_calendar.settings")} />
      <Step step={step} t={t} />
      <h1 className={styles.simpleTitle}>{t("sonic_calendar.link_title")}</h1>
      <p className={styles.hint}>{t("sonic_calendar.link_desc")}</p>
      <div className={styles.linkRow}>
        <span className={styles.linkPrefix}>{prefix}</span>
        <Input className={styles.bigField} value={value} maxLength={48} aria-label={t("sonic_calendar.link_title")} onChange={(e) => setValue(cleanSlug(e.target.value))} />
      </div>
      <div className={styles.buttonRow}>
        <Button type="primary" size="large" className={styles.cta} disabled={!valid} loading={saving} onClick={() => onDone(name)}>
          {t(step ? "sonic_calendar.done" : "sonic_calendar.save")}
        </Button>
        <Button
          size="large"
          disabled={!valid}
          onClick={() => {
            if (navigator.clipboard) navigator.clipboard.writeText(`${base}/${name}`);
            message.success(t("sonic_calendar.copied"));
          }}
        >
          {t("sonic_calendar.copy_link")}
        </Button>
        {savedUrl && !step ? (
          <Button size="large" href={savedUrl} target="_blank" rel="noopener noreferrer">
            {t("sonic_calendar.open_link")}
          </Button>
        ) : null}
      </div>
    </div>
  );
};

export const PROVIDERS = [
  { id: "google", icon: <GoogleOutlined />, name: "sonic_calendar.google" },
  { id: "microsoft", icon: <WindowsOutlined />, name: "sonic_calendar.outlook" },
  { id: "calendly", icon: <CalendarOutlined />, name: "sonic_calendar.calendly" },
];

/** "Which calendar do you use?": Google, Outlook, Calendly, or none (then Sonic Calendar is the calendar); once connected, who it is and a way out. */
export const CalendarScreen = ({ botId, connection, providers, step, onConnect, onCalendly, onNone, onNext, onDisconnect, onBack, t }) => {
  const [calendlyOpen, setCalendlyOpen] = useState(false);
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const current = PROVIDERS.find((p) => p.id === connection?.provider);

  const saveCalendly = async () => {
    setBusy(true);
    try {
      const res = await connectCalendly(botId, token.trim());
      setCalendlyOpen(false);
      setToken("");
      message.success(t("sonic_calendar.calendly_connected"));
      onCalendly(res.connection);
    } catch (err) {
      message.error(t(err.code === "calendly_token_invalid" ? "sonic_calendar.calendly_token_invalid" : "sonic_calendar.connect_error"));
    } finally {
      setBusy(false);
    }
  };

  const pick = (id) => (id === "calendly" ? setCalendlyOpen(true) : onConnect(id));

  return (
    <div className={styles.simple}>
      <Back onBack={onBack} label={t(step ? "sonic_calendar.public_back" : "sonic_calendar.settings")} />
      <Step step={step} t={t} />
      <h1 className={styles.simpleTitle}>{t(connection && current ? "sonic_calendar.your_calendar" : "sonic_calendar.q_calendar")}</h1>
      {connection && current ? (
        <>
          <div className={styles.bigChoice}>
            <span className={styles.choiceIcon}>{current.icon}</span>
            <span className={styles.choiceText}>
              {t(current.name)}
              <span>{connection.status === "connected" ? connection.account_email : t("sonic_calendar.reconnect_needed")}</span>
            </span>
          </div>
          {connection.status !== "connected" ? <p className={styles.hint}>{t("sonic_calendar.reconnect_hint")}</p> : null}
          <div className={styles.buttonRow}>
            {onNext && connection.status === "connected" ? (
              <Button type="primary" size="large" className={styles.cta} onClick={onNext}>
                {t("sonic_calendar.next")}
              </Button>
            ) : null}
            {connection.status !== "connected" ? (
              <Button type="primary" size="large" className={styles.cta} onClick={() => pick(current.id)}>
                {t("sonic_calendar.reconnect")}
              </Button>
            ) : null}
            <Popconfirm title={t("sonic_calendar.disconnect_confirm")} onConfirm={onDisconnect}>
              <Button size="large">{t("sonic_calendar.disconnect")}</Button>
            </Popconfirm>
          </div>
        </>
      ) : (
        <>
          <div className={styles.bigChoices}>
            {PROVIDERS.map((p) => (
              <button key={p.id} type="button" className={styles.bigChoice} disabled={!providers[p.id]} onClick={() => pick(p.id)}>
                <span className={styles.choiceIcon}>{p.icon}</span>
                <span className={styles.choiceText}>{t(p.name)}</span>
              </button>
            ))}
            {onNone ? (
              <button type="button" className={styles.bigChoice} onClick={onNone}>
                <span className={styles.choiceIcon}>
                  <ClockCircleOutlined />
                </span>
                <span className={styles.choiceText}>
                  {t("sonic_calendar.no_calendar_choice")}
                  <span>{t("sonic_calendar.no_calendar_sub")}</span>
                </span>
              </button>
            ) : null}
          </div>
        </>
      )}
      <Modal
        open={calendlyOpen}
        title={t("sonic_calendar.connect_calendly")}
        onCancel={() => setCalendlyOpen(false)}
        onOk={saveCalendly}
        okText={t("sonic_calendar.connect_calendly")}
        okButtonProps={{ disabled: token.trim().length < 20, loading: busy }}
      >
        <p>{t("sonic_calendar.calendly_token_help")}</p>
        <p>
          <a href="https://calendly.com/integrations/api_webhooks" target="_blank" rel="noopener noreferrer">
            calendly.com/integrations/api_webhooks
          </a>
        </p>
        <Input.Password
          value={token}
          placeholder={t("sonic_calendar.calendly_token_placeholder")}
          aria-label={t("sonic_calendar.calendly_token_placeholder")}
          onChange={(e) => setToken(e.target.value)}
        />
        <p className={styles.notice}>{t("sonic_calendar.calendly_plan_note")}</p>
      </Modal>
    </div>
  );
};

const statusLabel = (row, t) => {
  if (row.status === "cancelled") {
    return t(row.cancelled_by === "customer" ? "sonic_calendar.status_cancelled_customer" : "sonic_calendar.status_cancelled_business");
  }
  return row.status === "confirmed" ? null : t(`sonic_calendar.status_${row.status}`);
};

/** The tab itself: upcoming appointments, one menu per row. */
/** The tab itself: upcoming appointments and blocked time, one menu per row; + Add for phone bookings and blocks. */
export const AppointmentsScreen = ({ botId, tz, enabled, blocks, canBlock, limitReached, onAdd, onRemoveBlock, onRaiseLimit, onSettings, t, staff = [] }) => {
  const [scope, setScope] = useState("upcoming");
  const [rows, setRows] = useState(null);
  const [moving, setMoving] = useState(null);

  const load = useCallback(() => {
    setRows(null);
    listBookings(botId, scope)
      // A cancelled appointment is not upcoming: it only shows under Past.
      .then((res) => setRows((res.bookings || []).filter((b) => scope === "past" || b.status !== "cancelled")))
      .catch(() => setRows([]));
  }, [botId, scope]);

  useEffect(() => {
    load();
  }, [load]);

  // Blocked time shows among upcoming appointments, in time order.
  const items = useMemo(() => {
    if (rows === null) return null;
    if (scope === "past") return rows;
    const now = Date.now();
    const upcomingBlocks = (blocks || []).filter((b) => new Date(b.end).getTime() > now).map((b) => ({ ...b, block: true, sortAt: b.start }));
    return [...rows.map((r) => ({ ...r, sortAt: r.start_at })), ...upcomingBlocks].sort((a, b) => new Date(a.sortAt) - new Date(b.sortAt));
  }, [rows, blocks, scope]);

  const act = async (fn, okKey) => {
    try {
      await fn();
      message.success(t(okKey));
      load();
    } catch (err) {
      message.error(err.code === "outside_no_show_window" ? t("sonic_calendar.no_show_closed") : t("sonic_calendar.save_error"));
    }
  };

  const menuFor = (row) => {
    if (row.status !== "confirmed") return null;
    if (row.no_show_until) {
      return [{ key: "no_show", label: t("sonic_calendar.mark_no_show") }];
    }
    return [
      { key: "move", label: t("sonic_calendar.move") },
      { key: "cancel", label: t("sonic_calendar.cancel"), danger: true },
    ];
  };

  const onMenu = (row, key) => {
    if (key === "move") setMoving(row);
    if (key === "cancel") {
      Modal.confirm({
        title: t("sonic_calendar.cancel_confirm"),
        okText: t("sonic_calendar.cancel"),
        okButtonProps: { danger: true },
        cancelText: t("sonic_calendar.keep"),
        onOk: () => act(() => cancelBooking(botId, row.id), "sonic_calendar.cancelled"),
      });
    }
    if (key === "no_show") {
      Modal.confirm({
        title: t("sonic_calendar.no_show_confirm"),
        okText: t("sonic_calendar.mark_no_show"),
        cancelText: t("sonic_calendar.keep"),
        onOk: () => act(() => markNoShow(botId, row.id), "sonic_calendar.no_show_done"),
      });
    }
  };

  return (
    <div className={styles.simpleWide}>
      <div className={styles.titleRow}>
        <h1 className={styles.simpleTitle}>{t(scope === "past" ? "sonic_calendar.past_appointments" : "sonic_calendar.appointments_title")}</h1>
        <div className={styles.buttonRow}>
          <Dropdown
            menu={{
              items: [
                { key: "appointment", label: t("sonic_calendar.add_appointment") },
                ...(canBlock ? [{ key: "block", label: t("sonic_calendar.block_time") }] : []),
              ],
              onClick: ({ key }) => onAdd(key),
            }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <Button type="primary" size="large" className={styles.cta} icon={<PlusOutlined />}>
              {t("sonic_calendar.add")}
            </Button>
          </Dropdown>
          <Button size="large" onClick={onSettings}>
            {t("sonic_calendar.settings")}
          </Button>
        </div>
      </div>
      {limitReached ? (
        <div className={styles.alert} role="status">
          <p>{t("sonic_calendar.limit_reached")}</p>
          <Button size="large" onClick={onRaiseLimit}>
            {t("sonic_calendar.raise_limit")}
          </Button>
        </div>
      ) : null}
      {!enabled ? <p className={styles.hint}>{t("sonic_calendar.booking_off")}</p> : null}
      {rows === null ? <Spin /> : null}
      {items && items.length === 0 ? (
        <p className={styles.hint}>{t(scope === "past" ? "sonic_calendar.no_past" : "sonic_calendar.no_appointments_yet")}</p>
      ) : null}
      {items && items.length ? (
        <div className={styles.list}>
          {items.map((row) => {
            if (row.block) {
              return (
                <div key={row.id} className={styles.listRow}>
                  <div className={styles.listWhen}>
                    <strong>{formatLocal(row.start, tz, { weekday: "short", day: "numeric", month: "short" })}</strong>
                    <span>
                      {formatLocal(row.start, tz, { hour: "2-digit", minute: "2-digit", hourCycle: "h23" })}–{formatLocal(row.end, tz, { hour: "2-digit", minute: "2-digit", hourCycle: "h23" })}
                    </span>
                  </div>
                  <div className={styles.listMain}>
                    <strong>{t("sonic_calendar.blocked")}</strong>
                    <span>{row.note || t("sonic_calendar.blocked_hint")}</span>
                  </div>
                  <Dropdown menu={{ items: [{ key: "remove", label: t("sonic_calendar.unblock") }], onClick: () => onRemoveBlock(row.id) }} trigger={["click"]} placement="bottomRight">
                    <Button type="text" size="large" icon={<MoreOutlined />} aria-label={t("sonic_calendar.actions")} />
                  </Dropdown>
                </div>
              );
            }
            const zone = row.timezone || tz;
            const menu = menuFor(row);
            const status = statusLabel(row, t);
            return (
              <div key={row.id} className={row.status === "cancelled" ? styles.listRowMuted : styles.listRow}>
                <div className={styles.listWhen}>
                  <strong>{formatLocal(row.start_at, zone, { weekday: "short", day: "numeric", month: "short" })}</strong>
                  <span>{formatLocal(row.start_at, zone, { hour: "2-digit", minute: "2-digit", hourCycle: "h23" })}</span>
                </div>
                <div className={styles.listMain}>
                  <strong>{[row.service_name, staff.find((m) => m.id === row.staff_id)?.name].filter(Boolean).join(" · ")}</strong>
                  <span>{[row.customer_name, row.customer_phone].filter(Boolean).join(" · ")}</span>
                </div>
                {status ? <span className={styles.listStatus}>{status}</span> : null}
                {menu ? (
                  <Dropdown menu={{ items: menu, onClick: ({ key }) => onMenu(row, key) }} trigger={["click"]} placement="bottomRight">
                    <Button type="text" size="large" icon={<MoreOutlined />} aria-label={t("sonic_calendar.actions")} />
                  </Dropdown>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}
      <button type="button" className={styles.textLink} onClick={() => setScope(scope === "past" ? "upcoming" : "past")}>
        {t(scope === "past" ? "sonic_calendar.show_upcoming" : "sonic_calendar.show_past")}
      </button>
      <MoveModal
        botId={botId}
        booking={moving}
        tz={tz}
        t={t}
        onClose={() => setMoving(null)}
        onMoved={() => {
          setMoving(null);
          load();
        }}
      />
    </div>
  );
};

const MoveModal = ({ botId, booking, tz, t, onClose, onMoved }) => {
  const [from, setFrom] = useState(dayjs());
  const [slots, setSlots] = useState(null);
  const [picked, setPicked] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!booking) return;
    setPicked(null);
    setSlots(null);
    previewSlots(botId, { serviceId: booking.service_id, from: from.format("YYYY-MM-DD"), days: 7 })
      .then((res) => setSlots(res.slots || []))
      .catch(() => setSlots([]));
  }, [botId, booking, from]);

  const save = async () => {
    setSaving(true);
    try {
      await rescheduleBooking(botId, booking.id, picked);
      message.success(t("sonic_calendar.moved"));
      onMoved();
    } catch (err) {
      message.error(err.code === "slot_taken" ? t("sonic_calendar.slot_taken") : t("sonic_calendar.save_error"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={!!booking}
      title={t("sonic_calendar.move_title")}
      onCancel={onClose}
      onOk={save}
      okButtonProps={{ disabled: !picked, loading: saving }}
      okText={t("sonic_calendar.move")}
    >
      <DatePicker value={from} onChange={(d) => d && setFrom(d)} allowClear={false} />
      {slots === null ? <Spin /> : <SlotPicker slots={slots} tz={tz} selected={picked} onPick={setPicked} emptyText={t("sonic_calendar.no_slots")} />}
    </Modal>
  );
};

const timeOptions = TIME_OPTIONS.slice(0, -1);

/** Add an appointment taken by phone or at the door: a day, an open time, the customer. */
export const AddAppointmentScreen = ({ botId, services, tz, onDone, onBack, t, locale, staff = [] }) => {
  const active = services.filter((x) => x.active !== false && x.name);
  const [serviceId, setServiceId] = useState(active[0]?.id);
  const [staffId, setStaffId] = useState(null);
  const people = staff.filter((m) => m.active !== false && (!m.service_ids || m.service_ids.includes(serviceId)));
  const [day, setDay] = useState(dayjs());
  const [slots, setSlots] = useState(null);
  const [picked, setPicked] = useState(null);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSlots(null);
    setPicked(null);
    previewSlots(botId, { serviceId, staffId, from: day.format("YYYY-MM-DD"), days: 1 })
      .then((res) => setSlots(res.slots || []))
      .catch(() => setSlots([]));
  }, [botId, serviceId, staffId, day]);

  const save = async () => {
    setSaving(true);
    try {
      await createBooking(botId, { serviceId, staffId, start: picked, name: name.trim(), phone: phone.trim() || null, locale });
      message.success(t("sonic_calendar.appointment_added"));
      onDone();
    } catch (err) {
      message.error(t(err.code === "slot_taken" ? "sonic_calendar.slot_taken" : "sonic_calendar.save_error"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.simple}>
      <Back onBack={onBack} label={t("sonic_calendar.appointments_title")} />
      <h1 className={styles.simpleTitle}>{t("sonic_calendar.add_appointment")}</h1>
      {active.length > 1 ? (
        <Select
          className={styles.bigField}
          value={serviceId}
          options={active.map((x) => ({ value: x.id, label: x.name }))}
          onChange={(v) => {
            setServiceId(v);
            setStaffId(null);
          }}
          aria-label={t("sonic_calendar.service_name")}
        />
      ) : null}
      {people.length ? (
        <Select
          className={styles.bigField}
          value={staffId || ""}
          options={[{ value: "", label: t("sonic_calendar.public_anyone") }, ...people.map((m) => ({ value: m.id, label: m.name }))]}
          onChange={(v) => setStaffId(v || null)}
          aria-label={t("sonic_calendar.staff_title")}
        />
      ) : null}
      <DatePicker className={styles.bigField} value={day} allowClear={false} format="ddd D MMM" disabledDate={(d) => d && d.isBefore(dayjs().startOf("day"))} onChange={(d) => d && setDay(d)} />
      {slots === null ? <Spin /> : <SlotPicker slots={slots} tz={tz} selected={picked} onPick={setPicked} emptyText={t("sonic_calendar.no_slots_day")} />}
      <Input className={styles.bigField} value={name} maxLength={120} placeholder={t("sonic_calendar.customer_name")} aria-label={t("sonic_calendar.customer_name")} onChange={(e) => setName(e.target.value)} />
      <Input className={styles.bigField} value={phone} maxLength={40} placeholder={t("sonic_calendar.customer_phone")} aria-label={t("sonic_calendar.customer_phone")} onChange={(e) => setPhone(e.target.value)} />
      <div>
        <Button type="primary" size="large" className={styles.cta} disabled={!picked || name.trim().length < 2} loading={saving} onClick={save}>
          {t("sonic_calendar.save")}
        </Button>
      </div>
    </div>
  );
};

/** Block time: a day and hours Sonic never offers (lunch, an errand, a booking taken elsewhere). */
export const BlockTimeScreen = ({ tz, saving, onDone, onBack, t }) => {
  const [day, setDay] = useState(dayjs());
  const [from, setFrom] = useState("12:00");
  const [to, setTo] = useState("13:00");
  const [note, setNote] = useState("");
  const valid = to > from;

  return (
    <div className={styles.simple}>
      <Back onBack={onBack} label={t("sonic_calendar.appointments_title")} />
      <h1 className={styles.simpleTitle}>{t("sonic_calendar.block_time")}</h1>
      <DatePicker className={styles.bigField} value={day} allowClear={false} format="ddd D MMM" disabledDate={(d) => d && d.isBefore(dayjs().startOf("day"))} onChange={(d) => d && setDay(d)} />
      <div className={styles.timeRow}>
        <Select className={styles.bigField} suffixIcon={null} value={from} options={timeOptions} onChange={setFrom} aria-label={t("sonic_calendar.from")} />
        <span>{t("sonic_calendar.to_word")}</span>
        <Select className={styles.bigField} suffixIcon={null} value={to} options={TIME_OPTIONS.slice(1)} onChange={setTo} aria-label={t("sonic_calendar.to")} />
      </div>
      <Input className={styles.bigField} value={note} maxLength={120} placeholder={t("sonic_calendar.block_note")} aria-label={t("sonic_calendar.block_note")} onChange={(e) => setNote(e.target.value)} />
      <div>
        <Button
          type="primary"
          size="large"
          className={styles.cta}
          disabled={!valid}
          loading={saving}
          onClick={() => {
            const date = day.format("YYYY-MM-DD");
            onDone({ id: newServiceId(), start: zonedIso(date, from, tz), end: zonedIso(date, to, tz), note: note.trim() });
          }}
        >
          {t("sonic_calendar.block")}
        </Button>
      </div>
    </div>
  );
};

/** Days off: whole days (holidays, a trip) when Sonic offers no times. */
export const DaysOffScreen = ({ overrides, saving, onDone, onBack, t }) => {
  const today = dayjs().format("YYYY-MM-DD");
  const [list, setList] = useState(() => overrides.filter((o) => o.closed && o.date >= today));
  const add = (range) => {
    if (!range || !range[0]) return;
    const dates = [];
    for (let d = range[0].startOf("day"); !d.isAfter(range[1] || range[0], "day") && dates.length < 62; d = d.add(1, "day")) dates.push(d.format("YYYY-MM-DD"));
    const have = new Set(list.map((o) => o.date));
    setList([...list, ...dates.filter((x) => !have.has(x)).map((date) => ({ date, closed: true, intervals: [], note: "" }))].sort((a, b) => a.date.localeCompare(b.date)));
  };

  return (
    <div className={styles.simple}>
      <Back onBack={onBack} label={t("sonic_calendar.settings")} />
      <h1 className={styles.simpleTitle}>{t("sonic_calendar.days_off")}</h1>
      <p className={styles.hint}>{t("sonic_calendar.days_off_desc")}</p>
      <div className={styles.chips}>
        {list.map((o) => (
          <span key={o.date} className={styles.chip}>
            {dayjs(o.date).format("ddd D MMM")}
            <button type="button" className={styles.quietIcon} aria-label={t("sonic_calendar.remove")} onClick={() => setList(list.filter((x) => x.date !== o.date))}>
              <CloseOutlined />
            </button>
          </span>
        ))}
        {list.length === 0 ? <span className={styles.weekClosed}>{t("sonic_calendar.no_days_off")}</span> : null}
      </div>
      <DatePicker.RangePicker
        className={styles.bigField}
        format="D MMM"
        value={null}
        placeholder={[t("sonic_calendar.add_day_off"), t("sonic_calendar.until_optional")]}
        allowEmpty={[false, true]}
        disabledDate={(d) => d && d.isBefore(dayjs().startOf("day"))}
        onChange={add}
      />
      <div>
        <Button
          type="primary"
          size="large"
          className={styles.cta}
          loading={saving}
          onClick={() => {
            // Past dates and special-hours days (More options) stay as they are.
            const off = new Set(list.map((o) => o.date));
            const kept = overrides.filter((o) => (!o.closed || o.date < today) && !off.has(o.date));
            onDone([...kept, ...list].sort((a, b) => a.date.localeCompare(b.date)));
          }}
        >
          {t("sonic_calendar.save")}
        </Button>
      </div>
    </div>
  );
};

/**
 * Staff: the people customers can book, each with the services they do, optionally their own hours, and
 * whether the connected calendar is theirs. Empty = one calendar for everything (the business alone).
 */
export const StaffScreen = ({ staff, services, calendarConnected, saving, onDone, onHours, onBack, t }) => {
  const [list, setList] = useState(() => (staff || []).map((m) => ({ ...m })));
  const active = services.filter((x) => x.active !== false && x.name);
  const set = (idx, patch) => setList(list.map((m, i) => (i === idx ? { ...m, ...patch } : m)));
  const does = (m, id) => !m.service_ids || m.service_ids.includes(id);
  const toggleService = (idx, id) => {
    const m = list[idx];
    const current = m.service_ids || active.map((x) => x.id);
    const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
    // Every service ticked = "all", so services added later are included too.
    set(idx, { service_ids: next.length === active.length ? null : next });
  };
  const clean = () => list.filter((m) => m.name && m.name.trim()).map((m) => ({ ...m, name: m.name.trim() }));
  const valid = list.every((m) => !m.name.trim() || !m.service_ids || m.service_ids.length > 0);

  return (
    <div className={styles.simple}>
      <Back onBack={onBack} label={t("sonic_calendar.settings")} />
      <h1 className={styles.simpleTitle}>{t("sonic_calendar.staff_title")}</h1>
      <p className={styles.hint}>{t("sonic_calendar.staff_desc")}</p>
      <div className={styles.list}>
        {list.map((m, idx) => (
          <div key={m.id} className={styles.staffCard} data-testid="staff-person">
            <div className={styles.timeRow}>
              <Input
                className={styles.bigField}
                value={m.name}
                maxLength={80}
                placeholder={t("sonic_calendar.staff_name")}
                aria-label={t("sonic_calendar.staff_name")}
                onChange={(e) => set(idx, { name: e.target.value })}
              />
              <Popconfirm title={t("sonic_calendar.staff_remove_confirm")} onConfirm={() => setList(list.filter((_, i) => i !== idx))} okText={t("sonic_calendar.remove")}>
                <button type="button" className={styles.quietIcon} aria-label={t("sonic_calendar.remove")}>
                  <CloseOutlined />
                </button>
              </Popconfirm>
            </div>
            {active.length > 1 ? (
              <div>
                <span className={styles.hint}>{t("sonic_calendar.staff_does")}</span>
                <div className={styles.chips}>
                  {active.map((svc) => (
                    <button
                      key={svc.id}
                      type="button"
                      aria-pressed={does(m, svc.id)}
                      className={does(m, svc.id) ? styles.dayToggleOn : styles.dayToggle}
                      onClick={() => toggleService(idx, svc.id)}
                    >
                      {svc.name}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
            <div className={styles.staffLine}>
              <span>{m.weekly_hours ? t("sonic_calendar.staff_own_hours") : t("sonic_calendar.staff_same_hours")}</span>
              {/* Hours are edited on their own screen; the list is saved first so nothing typed here is lost. */}
              <button type="button" className={styles.textLink} disabled={!m.name.trim()} onClick={() => onHours(clean(), m.id)}>
                {t("sonic_calendar.staff_set_hours")}
              </button>
              {m.weekly_hours ? (
                <button type="button" className={styles.textLink} onClick={() => set(idx, { weekly_hours: null })}>
                  {t("sonic_calendar.staff_use_business_hours")}
                </button>
              ) : null}
            </div>
            {calendarConnected ? (
              <label className={styles.staffLine}>
                <Switch size="small" checked={!!m.uses_connected_calendar} onChange={(v) => set(idx, { uses_connected_calendar: v })} />
                <span>{t("sonic_calendar.staff_connected_calendar")}</span>
              </label>
            ) : null}
          </div>
        ))}
        {list.length === 0 ? <span className={styles.weekClosed}>{t("sonic_calendar.staff_none")}</span> : null}
      </div>
      <div>
        <Button
          icon={<PlusOutlined />}
          size="large"
          disabled={list.length >= 30}
          onClick={() => setList([...list, { id: newServiceId(), name: "", active: true, service_ids: null, weekly_hours: null, uses_connected_calendar: false }])}
        >
          {t("sonic_calendar.staff_add")}
        </Button>
      </div>
      <div>
        <Button type="primary" size="large" className={styles.cta} disabled={!valid} loading={saving} onClick={() => onDone(clean())}>
          {t("sonic_calendar.save")}
        </Button>
      </div>
    </div>
  );
};

/** Settings: booking on/off and one row per setup answer, plus More options. */
export const SettingsScreen = ({ enabled, saving, onToggle, rows, onOpen, onMore, onBack, t }) => (
  <div className={styles.simple}>
    <Back onBack={onBack} label={t("sonic_calendar.appointments_title")} />
    <h1 className={styles.simpleTitle}>{t("sonic_calendar.settings")}</h1>
    <div className={styles.list}>
      <div className={styles.settingsRow}>
        <div className={styles.listMain}>
          <strong>{t("sonic_calendar.enable_short")}</strong>
          <span>{t(enabled ? "sonic_calendar.on" : "sonic_calendar.off")}</span>
        </div>
        <Switch checked={enabled} loading={saving} onChange={onToggle} aria-label={t("sonic_calendar.enable_short")} />
      </div>
      {rows.map((row) =>
        row.view ? (
          <button key={row.key} type="button" className={styles.settingsRowButton} onClick={() => onOpen(row.view)}>
            <span className={styles.listMain}>
              <strong>{row.title}</strong>
              <span>{row.value}</span>
            </span>
            <span className={styles.chev} aria-hidden="true">
              ›
            </span>
          </button>
        ) : (
          <div key={row.key} className={styles.settingsRow}>
            <div className={styles.listMain}>
              <strong>{row.title}</strong>
              <span>{row.value}</span>
            </div>
          </div>
        )
      )}
    </div>
    <button type="button" className={styles.textLink} onClick={onMore}>
      {t("sonic_calendar.more_options")}
    </button>
  </div>
);
